import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import SideNavigationBar from "../reusables/SideNavigationBar/SideNavigationBar";

const API_BASE = `${process.env.REACT_APP_API_URL}/user/event/event-types`;
// const API_BASE = `http://192.168.0.5:5443/api/user/event/event-types`;

// Reusable Modal component
function Modal({ isOpen, title, children, onClose }) {
  if (!isOpen) return null;
  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h3 style={styles.modalTitle}>{title}</h3>
          <button style={styles.closeBtn} onClick={onClose}>
            &times;
          </button>
        </div>
        <div style={styles.modalContent}>{children}</div>
      </div>
    </div>
  );
}

export default function EventTypeManager() {
  const [eventTypes, setEventTypes] = useState([]);
  const [form, setForm] = useState({
    type: "",
    title: "",
    description: "",
    fixture: "",
    to_display: false,
  });
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchEventTypes();
  }, []);

  async function fetchEventTypes() {
    try {
      const res = await fetch(API_BASE);
      const { events } = await res.json();
      setEventTypes(events.sort((a, b) => a.display_order - b.display_order));
    } catch (err) {
      console.error("Failed to fetch event types", err);
    }
  }

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);

    const method = editId ? "PUT" : "POST";
    const url = editId ? `${API_BASE}/${editId}` : API_BASE;
    const displayOrderIndex = editId
      ? eventTypes.findIndex((item) => item.id === editId)
      : eventTypes.length;
    const payload = {
      ...form,
      to_display: form.to_display ? 1 : 0,
      display_order: displayOrderIndex + 1,
    };

    try {
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const saved = await res.json(); // server should return full item

      // Optimistically update local state without refetch
      setEventTypes((prev) => {
        if (editId) {
          return prev.map((item) =>
            item.id === editId ? { ...item, ...payload, ...saved } : item
          );
        } else {
          return [...prev, { ...payload, ...saved }];
        }
      });

      closeForm();
    } catch (err) {
      console.error("Save failed:", err);
    } finally {
      setLoading(false);
    }
  }

  function openForm(et = null) {
    if (et) {
      setForm({
        type: et.type,
        title: et.title,
        description: et.description,
        fixture: et.fixture || "", // Ensure fixture has a default value
        to_display: !!et.to_display,
        speakerform_title: et.speakerform_title,
        speakerform_description: et.speakerform_description,

        cohostform_title: et.cohostform_title,
        cohostform_description: et.cohostform_description,
      });
    } else {
      setForm({
        type: "",
        title: "",
        description: "",
        fixture: "",
        to_display: false,
      });
    }
    setEditId(et?.id || null);
    setIsModalOpen(true);
  }

  function closeForm() {
    setIsModalOpen(false);
    setEditId(null);
  }

  async function handleDelete(id) {
    if (!window.confirm("This action cannot be undone. Delete?")) return;
    try {
      await fetch(`${API_BASE}/${id}`, { method: "DELETE" });
      fetchEventTypes(); // Refetch to get the updated list
    } catch (err) {
      console.error("Delete failed:", err);
    }
  }

  function onDragEnd(result) {
    if (!result.destination) return;
    const items = Array.from(eventTypes);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);

    const reordered = items.map((it, idx) => ({
      ...it,
      display_order: idx + 1,
    }));
    setEventTypes(reordered);
    // Optionally batch update display_order via API
    reordered.forEach((it) => {
      fetch(`${API_BASE}/${it.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ display_order: it.display_order }),
      }).catch(console.error);
    });
  }

  return (
    <div style={styles.container}>
      <SideNavigationBar />
      <header style={styles.header}>
        <h2 style={styles.title}>Event Type Manager</h2>
        <button
          style={{ ...styles.button, ...styles.primary }}
          onClick={() => openForm()}
        >
          + New Event Type
        </button>
      </header>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="list">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {eventTypes.map((et, idx) => (
                <Draggable key={et.id} draggableId={`${et.id}`} index={idx}>
                  {(prov) => (
                    <div
                      ref={prov.innerRef}
                      {...prov.draggableProps}
                      {...prov.dragHandleProps}
                      style={{ ...styles.card, ...prov.draggableProps.style }}
                    >
                      <div>
                        <h4 style={styles.cardTitle}>{et.type}</h4>
                        <p style={styles.cardSubtitle}>{et.title}</p>
                        <p style={{ fontSize: "14px", margin: 2 }}>
                          {et.description}
                        </p>
                        <p
                          style={{
                            fontSize: "14px",
                            margin: 2,
                            fontWeight: "bold",
                          }}
                        >
                          Fixture: {et.fixture}
                        </p>{" "}
                        {/* Display fixture */}
                      </div>
                      <div>
                        <button
                          style={styles.smallBtn}
                          onClick={() => openForm(et)}
                        >
                          Edit
                        </button>
                        <button
                          style={{ ...styles.smallBtn, ...styles.danger }}
                          onClick={() => handleDelete(et.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <Modal
        isOpen={isModalOpen}
        title={editId ? "Edit Event Type" : "Add Event Type"}
        onClose={closeForm}
      >
        <form onSubmit={handleSubmit} style={styles.form}>
          <label htmlFor="type">Type</label>
          <input
            id="type"
            name="type"
            placeholder="Type"
            value={form.type}
            onChange={handleChange}
            required
            style={styles.input}
          />
          <label htmlFor="title">Title</label>
          <input
            id="title"
            name="title"
            placeholder="Title"
            value={form.title}
            onChange={handleChange}
            required
            style={styles.input}
          />
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            name="description"
            placeholder="Description"
            value={form.description}
            onChange={handleChange}
            style={styles.textarea}
          />
          <label htmlFor="fixture">Fixture</label> {/* Fixture input */}
          <input
            id="fixture"
            name="fixture"
            placeholder="Fixture"
            value={form.fixture}
            onChange={handleChange}
            style={styles.input}
          />
          <label htmlFor="fixture">Speaker/Recruiter Title</label>{" "}
          {/* Fixture input */}
          <input
            id="speakerform_title"
            name="speakerform_title"
            placeholder="speakerform_title"
            value={form.speakerform_title}
            onChange={handleChange}
            style={styles.input}
          />{" "}
          <label htmlFor="fixture">Speaker/Recruiter Description</label>{" "}
          {/* Fixture input */}
          <textarea
            id="speakerform_description"
            name="speakerform_description"
            placeholder="speakerform_description"
            value={form.speakerform_description}
            onChange={handleChange}
            style={styles.textarea}
          />{" "}
          <label htmlFor="fixture">Co-Host Title</label> {/* Fixture input */}
          <input
            id="cohostform_title"
            name="cohostform_title"
            placeholder="cohostform_title"
            value={form.cohostform_title}
            onChange={handleChange}
            style={styles.input}
          />{" "}
          <label htmlFor="fixture">Co-Host description</label>{" "}
          {/* Fixture input */}
          <textarea
            id="cohostform_description"
            name="cohostform_description"
            placeholder="cohostform_description"
            value={form.cohostform_description}
            onChange={handleChange}
            style={styles.textarea}
          />
          <div style={styles.rowCenter}>
            <label style={styles.checkboxLabel}>
              <input
                name="to_display"
                type="checkbox"
                checked={form.to_display}
                onChange={handleChange}
              />{" "}
              Show
            </label>
          </div>
          <div style={styles.actions}>
            <button
              type="submit"
              style={{ ...styles.button, ...styles.primary }}
              disabled={loading}
            >
              {editId ? "Update" : "Create"}
            </button>
            <button
              type="button"
              onClick={closeForm}
              style={{ ...styles.button, ...styles.secondary }}
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: 800,
    margin: "auto",
    fontFamily: "Arial, sans-serif",
    padding: "20px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  title: { margin: 0, color: "#007acc" },
  button: {
    padding: "10px 20px",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    fontSize: 14,
    transition: "transform .2s",
  },
  primary: {
    background: "linear-gradient(133deg, #2AA8C4 16.39%, #38C1DF 100%)",
    color: "#fff",
  },
  secondary: { backgroundColor: "#e0e0e0", color: "#333" },
  danger: {
    background: "linear-gradient(90deg, #ff416c, #ff4b2b)",
    color: "#fff",
  },
  card: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 15,
    borderRadius: 8,
    marginBottom: 10,
    background: "#ffffff",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
  },
  cardTitle: { margin: "0 0 5px", fontSize: 18, color: "#333" },
  cardSubtitle: { margin: 0, fontSize: 16, color: "#555" },
  smallBtn: {
    padding: "6px 12px",
    fontSize: 12,
    borderRadius: 4,
    marginBottom: 10,
    border: "none",
    cursor: "pointer",
    marginLeft: 5,
    backgroundColor: "#2AA8C4",
    color: "#fff",
  },
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  },
  modal: {
    backgroundColor: "#fff",
    borderRadius: 8,
    width: "100%",
    maxWidth: 500,
    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
    overflow: "hidden",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "10px 20px",
    background: "linear-gradient(133deg, #2AA8C4 16.39%, #38C1DF 100%)",
    color: "#fff",
  },
  modalTitle: { margin: 0, fontSize: 18 },
  closeBtn: {
    background: "none",
    border: "none",
    fontSize: 24,
    color: "#fff",
    cursor: "pointer",
  },
  modalContent: { padding: 20 },
  form: {
    display: "flex",
    flexDirection: "column",
    maxHeight: "80vh", // or whatever suits your layout
    overflowY: "auto",
    paddingRight: "1rem",
    gap: 15,
  },
  input: {
    width: "100%",
    padding: 10,
    fontSize: 14,
    borderRadius: 4,
    border: "1px solid #ccc",
  },
  textarea: {
    width: "100%",
    padding: 10,
    fontSize: 14,
    borderRadius: 4,
    border: "1px solid #ccc",
    minHeight: 60,
  },
  rowCenter: { display: "flex", alignItems: "center" },
  checkboxLabel: { fontSize: 14, cursor: "pointer" },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 10,
    gap: "16px",
  },
};
